import { ArrowUpRight } from "lucide-react";

const HEAD = "Give AI a shared understanding of your business.";
const SUB = "Generate a semantic and contextual knowledge layer connecting enterprise data, metrics, entities, relationships, documents and institutional knowledge.";
const CAPS = [
  { title: "Context Generation", desc: "Continuously discover and create enterprise context. Connect data, activity, business processes, policies, documents and institutional knowledge to automatically map the relationships and meaning behind your business." },
  { title: "Context Persistence", desc: "Make enterprise knowledge persistent and adaptive. Store structured context across knowledge graphs, vector stores and enterprise memory — preserving organizational, user and interaction context over time." },
  { title: "Context Validation", desc: "Keep context trusted, complete and current. Continuously evaluate context for completeness, consistency, conflicts, coverage and freshness so AI operates on reliable business understanding." },
  { title: "Context Retrieval", desc: "Deliver the right context at the right moment. Discover, rank and assemble the most relevant context for every query, agent and task — continuously learning and improving from usage." },
];

const Thumb = ({ full }) => (
  <div className={`group flex flex-col rounded-xl overflow-hidden border border-[#3E2818]/12 shadow-[0_18px_48px_-30px_rgba(62,40,24,0.5)] ${full ? "" : "h-full"}`}>
    <div className="shrink-0 flex items-center gap-2 px-3.5 py-2.5 bg-[#F1EDE6] border-b border-[#3E2818]/8">
      <span className="w-2.5 h-2.5 rounded-full bg-[#E88B6B]" /><span className="w-2.5 h-2.5 rounded-full bg-[#E8C15E]" /><span className="w-2.5 h-2.5 rounded-full bg-[#9CB88A]" />
      <span className="ml-2 flex-1 truncate rounded-md bg-white/70 px-2.5 py-1 font-mono2 text-[10px] text-[#3E2818]/55">graph.demo.intugle.ai</span>
    </div>
    <div className={`relative overflow-hidden bg-white ${full ? "aspect-[16/8]" : "flex-1 aspect-[16/11] lg:aspect-auto"}`}>
      <img src="/assets/enterprise-brain-demo.png" alt="demo" className="absolute inset-0 w-full h-full object-cover object-top" />
      <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#F7F5F0] via-[#F7F5F0]/70 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-4 flex items-end justify-between">
        <div>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#8A5730]">Knowledge Graph Explorer</p>
          <p className="text-[15px] font-semibold text-[#3E2818] mt-0.5">Explore the live demo</p>
        </div>
        <span className="w-9 h-9 rounded-full bg-[#211307] text-white flex items-center justify-center"><ArrowUpRight size={18} /></span>
      </div>
    </div>
  </div>
);

const Header = () => (
  <>
    <h3 className="font-display text-2xl lg:text-3xl font-medium text-[#3E2818] tracking-tight leading-tight">{HEAD}</h3>
    <p className="text-sm md:text-base text-[#3E2818]/65 mt-3 leading-relaxed max-w-3xl">{SUB}</p>
  </>
);

const CapCard = ({ c, compact }) => (
  <div className="rounded-xl bg-white border border-[#3E2818]/10 p-4">
    <p className="text-[13px] font-semibold text-[#3E2818] leading-snug">{c.title}</p>
    <p className={`text-[12px] text-[#3E2818]/60 mt-1.5 leading-relaxed ${compact ? "line-clamp-none" : ""}`}>{c.desc}</p>
  </div>
);

/* A · Hero split (header+thumb) then 4-col capability strip */
const A = () => (
  <div>
    <div className="grid lg:grid-cols-[1fr_0.8fr] gap-8 items-center">
      <div><Header /></div>
      <Thumb full />
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
      {CAPS.map((c) => <CapCard key={c.title} c={c} />)}
    </div>
  </div>
);

/* B · Thumb full-width on top, capabilities 4-col below */
const B = () => (
  <div>
    <Header />
    <div className="mt-6"><Thumb full /></div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
      {CAPS.map((c) => <CapCard key={c.title} c={c} />)}
    </div>
  </div>
);

/* C · Header top full width, then 2x2 caps left / thumb right (natural, top-aligned) */
const C = () => (
  <div>
    <Header />
    <div className="grid lg:grid-cols-[1fr_0.55fr] gap-8 mt-6 items-start">
      <div className="grid sm:grid-cols-2 gap-3">
        {CAPS.map((c) => <CapCard key={c.title} c={c} />)}
      </div>
      <Thumb full />
    </div>
  </div>
);

/* D · Header top, capabilities as vertical list left / thumb right */
const D = () => (
  <div>
    <Header />
    <div className="grid lg:grid-cols-[1fr_0.6fr] gap-8 mt-6 items-start">
      <div className="flex flex-col gap-2.5">
        {CAPS.map((c, i) => (
          <div key={c.title} className="rounded-xl bg-white border border-[#3E2818]/10 p-4 flex gap-3.5">
            <span className="font-mono2 text-[11px] text-[#8A5730] pt-0.5">{String(i + 1).padStart(2, "0")}</span>
            <div>
              <p className="text-[13px] font-semibold text-[#3E2818]">{c.title}</p>
              <p className="text-[12px] text-[#3E2818]/60 mt-1 leading-relaxed">{c.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="lg:sticky lg:top-24"><Thumb full /></div>
    </div>
  </div>
);

/* E · Header + full-width thumb hero, capabilities as 2-col list below */
const E = () => (
  <div>
    <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 items-end">
      <div><Header /></div>
      <Thumb full />
    </div>
    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5 mt-8">
      {CAPS.map((c, i) => (
        <div key={c.title} className="border-t border-[#3E2818]/12 pt-4">
          <p className="text-[13px] font-semibold text-[#3E2818]"><span className="font-mono2 text-[11px] text-[#8A5730] mr-2">{String(i + 1).padStart(2, "0")}</span>{c.title}</p>
          <p className="text-[12px] text-[#3E2818]/60 mt-1.5 leading-relaxed">{c.desc}</p>
        </div>
      ))}
    </div>
  </div>
);

const OPTIONS = [
  { id: "a", title: "Option A · Hero Split + Capability Strip", note: "Header and demo sit side-by-side as a hero; the 4 capabilities run full-width as a clean 4-column strip beneath. Balanced and scannable.", comp: A },
  { id: "b", title: "Option B · Demo on Top, Capabilities Below", note: "Header, then the demo full-width, then capabilities in a 4-column row. Puts the product front and centre.", comp: B },
  { id: "c", title: "Option C · Header Top, 2×2 + Thumbnail", note: "Your current pieces, re-balanced: header spans the top, then a 2×2 capability grid beside a natural-height thumbnail (no awkward stretch).", comp: C },
  { id: "d", title: "Option D · Numbered List + Sticky Demo", note: "Capabilities as a numbered vertical list on the left; the demo stays sticky on the right as you read. Editorial and easy to follow.", comp: D },
  { id: "e", title: "Option E · Hero + Editorial Capability Rows", note: "Header + large demo hero on top; capabilities become clean 2-column text rows with hairlines and numbers. Most magazine-like.", comp: E },
];

export default function EnterpriseBrainOptions() {
  return (
    <div className="min-h-screen bg-[#EFE7D9]">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">Enterprise Brain Detail · Layout Options</p>
        <h1 className="font-display text-2xl lg:text-3xl font-medium mt-2 tracking-tight text-[#3E2818]">Pick a layout for the solution detail</h1>
        <p className="text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">Shown on the Section 5 background with the real Enterprise Brain content. Tell me the letter and I&rsquo;ll apply it (it&rsquo;ll drive the layout for the other solutions too).</p>

        <div className="mt-10 space-y-16">
          {OPTIONS.map((o) => {
            const C = o.comp;
            return (
              <div key={o.id}>
                <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#B87A32] mb-4">{o.title}</p>
                <div className="rounded-2xl bg-[#EFE7D9] p-5 lg:p-7 border border-[#3E2818]/8">
                  <C />
                </div>
                <p className="text-[12px] text-[#3E2818]/55 mt-3 leading-snug max-w-3xl">{o.note}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
